import { useMemo, useState } from 'react';
import { useAuth } from '../../../utils/Auth';
import styles from './PlanSelector.module.css';

// Determine support label based on support level
const getSupportLabel = (supportLevel) => {
  if (supportLevel === 1) {
    return 'Basic Support';
  }
  return 'Premium Support';
};

// Determine analytics label based on analytics level
const getAnalyticsLabel = (analyticsLevel) => {
  if (analyticsLevel === 1) {
    return 'Basic Analytics';
  }
  return 'Advanced Analytics';
};

const PlanSelector = ({ onSelectPlan }) => {
  const { config } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // Get active plans (excluding free tier)
  const plans = useMemo(() => {
    if (!config?.plans) return [];
    
    return Object.entries(config.plans)
      .filter(([key, plan]) => key !== 'free' && plan.active)
      .map(([key, plan]) => ({ planId: key, ...plan }))
      .sort((a, b) => a.liquid_qrs - b.liquid_qrs); // Sort by features (ascending)
  }, [config.plans]);

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
    onSelectPlan({ planId, billingCycle });
  };

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    // If a plan is already selected, update the callback with new billing cycle
    if (selectedPlanId) {
      onSelectPlan({ planId: selectedPlanId, billingCycle: cycle });
    }
  };

  return (
    <div className={styles.selectorContainer}>
      <p className={styles.sectionKicker}>Plan selection</p>
      <h2 className={styles.sectionTitle}>Choose your plan</h2>
      <p className={styles.sectionSubtitle}>
        Select the plan that best fits your needs. You can always upgrade or downgrade later.
      </p>

      {/* Billing Cycle Toggle */}
      <div className={styles.billingToggleContainer}>
        <div className={styles.billingToggle}>
          <button
            type="button"
            className={`${styles.toggleButton} ${billingCycle === 'monthly' ? styles.active : ''}`}
            onClick={() => handleBillingCycleChange('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`${styles.toggleButton} ${billingCycle === 'annual' ? styles.active : ''}`}
            onClick={() => handleBillingCycleChange('annual')}
          >
            Annual
          </button>
        </div>
        <a href="https://liquidqr.com/pricing/" className={styles.pricingLink} target="_blank" rel="noopener noreferrer">
          View detailed pricing
        </a>
      </div>

      {/* Plans Grid */}
      <div className={styles.plansGrid}>
        {plans.map(({ planId, ...plan }) => (
          <label key={planId} className={`${styles.planCard} ${selectedPlanId === planId ? styles.selected : ''}`}>
            <input
              type="radio"
              name="plan"
              value={planId}
              checked={selectedPlanId === planId}
              onChange={() => handleSelectPlan(planId)}
              className={styles.radioInput}
            />
            <div className={styles.planContent}>
              <h3 className={styles.planName}>{plan.name}</h3>
              
              {/* Price - always show monthly price */}
              <div className={styles.priceContainer}>
                <span className={styles.price}>${plan.stripe_prices[billingCycle].cost}</span>
                <span className={styles.billingPeriod}>/month</span>
              </div>

              {/* Plan Features */}
              <ul className={styles.featuresList}>
                <li>
                  <span className={styles.featureIcon}>✓</span>
                  <span>{plan.liquid_qrs} QR Codes</span>
                </li>
                <li>
                  <span className={styles.featureIcon}>✓</span>
                  <span>{plan.categories} Categories</span>
                </li>
                <li>
                  <span className={styles.featureIcon}>✓</span>
                  <span>{plan.team_size} Team {plan.team_size === 1 ? 'Member' : 'Members'}</span>
                </li>
                {plan.analytics > 0 && (
                  <li>
                    <span className={styles.featureIcon}>✓</span>
                    <span>{getAnalyticsLabel(plan.analytics)}</span>
                  </li>
                )}
                {plan.support > 0 && (
                  <li>
                    <span className={styles.featureIcon}>✓</span>
                    <span>{getSupportLabel(plan.support)}</span>
                  </li>
                )}
              </ul>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};

export default PlanSelector;
